import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/services/api';
import type { ArticleReportResponse } from '@/types/api';

export function ArticleDetail() {
  const { websiteId, articleId } = useParams<{ websiteId: string; articleId: string }>();
  const [data, setData] = useState<ArticleReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!articleId) return;
      try {
        const response = await api.getArticleReport(articleId);
        setData(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [articleId]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="h-4 w-48 bg-gray-200 rounded animate-pulse mb-4" />
          <div className="h-32 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error || 'Failed to load article'}</p>
      </div>
    );
  }

  const { article, statistics, insights } = data;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Link
          to={`/websites/${websiteId}/articles`}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{article.title}</h1>
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {article.url}
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Score Overview */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Sincerity Score</h3>
            <div className="flex items-center gap-6">
              <div
                className={`text-5xl font-bold ${
                  (article.actualRating ?? 0) >= 70
                    ? 'text-green-600'
                    : (article.actualRating ?? 0) >= 50
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }`}
              >
                {article.actualRating ?? '-'}
              </div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      (article.actualRating ?? 0) >= 70
                        ? 'bg-green-500'
                        : (article.actualRating ?? 0) >= 50
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                    }`}
                    style={{ width: `${article.actualRating ?? 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Analysis Breakdown */}
          {article.analysisResult && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Analysis Breakdown</h3>
              <div className="space-y-4">
                {[
                  { label: 'AI Content Detection', value: 100 - article.analysisResult.aiScore, inverted: true },
                  { label: 'Style Consistency', value: article.analysisResult.styleScore },
                  { label: 'Hard Paste Detection', value: 100 - article.analysisResult.hardPasteScore, inverted: true },
                  { label: 'AI Image Detection', value: 100 - article.analysisResult.aiImageScore, inverted: true },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-medium">{item.value}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          item.value >= 70
                            ? 'bg-green-500'
                            : item.value >= 50
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                        }`}
                        style={{ width: `${item.value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Insights */}
          {insights.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Insights</h3>
              <ul className="space-y-2">
                {insights.map((insight, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-600">{insight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Vote Statistics */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Visitor Votes</h3>
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{statistics.upvotes}</div>
                <div className="text-sm text-gray-500">Upvotes</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">{statistics.downvotes}</div>
                <div className="text-sm text-gray-500">Downvotes</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Approval Rate</span>
                <span className="font-medium">{Math.round(statistics.voteRatio * 100)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500"
                  style={{ width: `${statistics.voteRatio * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Meta Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Details</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Self Rating</dt>
                <dd className="font-medium">{article.selfRating ?? 'Not set'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Revisions</dt>
                <dd className="font-medium">{article.revisionCount}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Published</dt>
                <dd className="font-medium">
                  {article.publishedAt ? new Date(article.publishedAt).toLocaleDateString() : 'Unknown'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Crawled</dt>
                <dd className="font-medium">
                  {new Date(article.createdAt).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Updated</dt>
                <dd className="font-medium">
                  {new Date(article.updatedAt).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
